import { useEffect, useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { JobListRow, JobMergePreview, JobMergeRepoint } from "@/types";
import { Button, Pill, Spinner, Toast, useToast } from "@/components/ui";
import { JobPicker } from "./JobPicker";
import "./review.css";

/* ─── helpers ──────────────────────────────────────────────────────────── */

/** A merge target must be a real numbered job — never another shell, never the
 *  shell itself. (`stage` is typed to the canonical set; shells carry off-vocab
 *  "active" at runtime, hence the cast.) */
function isRealJob(job: JobListRow, shellId: number): boolean {
  return job.job_number > 0 && (job.stage as string) !== "active" && job.id !== shellId;
}

function repointTotal(r: JobMergeRepoint): number {
  return r.application + r.task + r.fee + r.invoice + r.quote + r.lead;
}

function repointBreakdown(r: JobMergeRepoint): string {
  const parts: string[] = [];
  if (r.application) parts.push(`${r.application} application${r.application === 1 ? "" : "s"}`);
  if (r.task) parts.push(`${r.task} task${r.task === 1 ? "" : "s"}`);
  if (r.fee) parts.push(`${r.fee} fee${r.fee === 1 ? "" : "s"}`);
  if (r.invoice) parts.push(`${r.invoice} invoice${r.invoice === 1 ? "" : "s"}`);
  if (r.quote) parts.push(`${r.quote} quote${r.quote === 1 ? "" : "s"}`);
  if (r.lead) parts.push(`${r.lead} lead${r.lead === 1 ? "" : "s"}`);
  return parts.join(", ");
}

/* ─── screen ───────────────────────────────────────────────────────────── */

/** Review › Jobs — the shell-job cleanup. Lists the auto-created shell jobs
 *  (job_number 0, off-vocab status "active") and merges each into the real job it
 *  duplicates (the surviving job is picked by hand; the dry-run previews it). */
export function ShellJobMerge() {
  const [shells, setShells] = useState<JobListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);
  const [pickFor, setPickFor] = useState<number | null>(null); // shell id we're choosing a target for
  const [mergeTarget, setMergeTarget] = useState<{ loser: JobListRow; winner: JobListRow } | null>(null);
  const { toast, showToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    jobsApi
      .listShellJobs()
      .then((res) => {
        if (!cancelled) setShells(res.items ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load shell jobs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return (
    <section className="review">
      <header className="review__head">
        <div>
          <h1>Shell jobs</h1>
          {!loading ? (
            <p className="review__count">
              {shells.length} auto-created shell job{shells.length === 1 ? "" : "s"} to merge
            </p>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
          Refresh
        </Button>
      </header>

      <p className="review__intro">
        These jobs were auto-created by the letter pipeline (no job number, status “active”). Merge each
        into the real numbered job it duplicates — its records move across, the real job’s address is
        backfilled, and the shell is archived.
      </p>

      {error ? (
        <p className="leads__error review__error" role="alert">
          {error}
          <button type="button" className="review__retry" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </button>
        </p>
      ) : null}

      {loading ? (
        <div className="review__loading">
          <Spinner label="Loading shell jobs…" />
        </div>
      ) : shells.length === 0 ? (
        <p className="review__empty">No shell jobs left — every job is a real numbered job.</p>
      ) : (
        <div className="review__section">
          {shells.map((shell) => (
            <article key={shell.id} className="review-app">
              <div className="review-app__id">
                <span className="review-app__ref">{shell.job_name || "(no name)"}</span>
                <Pill tone="neutral">Auto-created shell</Pill>
                {shell.postcode ? <span className="review-app__was">{shell.postcode}</span> : null}
              </div>
              <div className="review-app__meta">
                <span>{shell.site_full_address || "—"}</span>
                <span>
                  {shell.open_application_count} application{shell.open_application_count === 1 ? "" : "s"} ·{" "}
                  {shell.open_task_count} task{shell.open_task_count === 1 ? "" : "s"} attached
                </span>
              </div>
              <div className="review-app__actions">
                {pickFor === shell.id ? (
                  <JobPicker
                    autoOpen
                    initialQuery={shell.job_name || shell.postcode || ""}
                    placeholder="Search the real job by #, name, address…"
                    filter={(job) => isRealJob(job, shell.id)}
                    onPick={(winner) => {
                      setMergeTarget({ loser: shell, winner });
                      setPickFor(null);
                    }}
                    onCancel={() => setPickFor(null)}
                  />
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => setPickFor(shell.id)}>
                    Merge into job…
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {mergeTarget ? (
        <JobMergeDialog
          winner={mergeTarget.winner}
          loser={mergeTarget.loser}
          onClose={() => setMergeTarget(null)}
          onMerged={(message) => {
            setMergeTarget(null);
            showToast(message);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

/* ─── merge preview + confirm dialog ───────────────────────────────────── */

function JobMergeDialog({
  winner,
  loser,
  onClose,
  onMerged,
}: {
  winner: JobListRow;
  loser: JobListRow;
  onClose: () => void;
  onMerged: (message: string) => void;
}) {
  const [preview, setPreview] = useState<JobMergePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    jobsApi
      .previewJobMerge(winner.id, loser.id)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not preview the merge.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [winner.id, loser.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !committing) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [committing, onClose]);

  async function confirm() {
    setCommitting(true);
    setError(undefined);
    try {
      const res = await jobsApi.mergeJobs(winner.id, loser.id);
      const moved = repointTotal(res.repointed);
      onMerged(
        moved > 0
          ? `Merged “${loser.job_name}” into job #${res.winner_job_number} (${moved} record${moved === 1 ? "" : "s"} moved)`
          : `Archived empty shell “${loser.job_name}” into job #${res.winner_job_number}`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not merge the jobs.");
      setCommitting(false);
    }
  }

  const moved = preview ? repointTotal(preview.repointed) : 0;
  const breakdown = preview ? repointBreakdown(preview.repointed) : "";

  return (
    <div className="review-modal__overlay" role="dialog" aria-modal="true" onClick={committing ? undefined : onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="review-modal__head">
          <h2>Merge shell job</h2>
          <span className="review-modal__key">{loser.job_name}</span>
        </div>

        <div className="review-modal__body">
          {loading ? (
            <Spinner label="Previewing merge…" />
          ) : error && !preview ? (
            <p className="leads__error" role="alert">{error}</p>
          ) : preview ? (
            <>
              <p>
                Keep <strong>job #{preview.winner_job_number}</strong> ({winner.job_name || "—"}) and merge the
                shell <strong>“{loser.job_name}”</strong> into it.
              </p>
              <ul className="review-modal__effects">
                {moved > 0 ? (
                  <li>Moves {breakdown} onto job #{preview.winner_job_number}.</li>
                ) : (
                  <li>The shell has no attached records — nothing to move.</li>
                )}
                {preview.winner_fields_to_backfill.length > 0 ? (
                  <li>Backfills the real job’s empty {preview.winner_fields_to_backfill.join(", ")} from the shell.</li>
                ) : null}
                <li>Archives the shell and marks it merged into #{preview.winner_job_number} (it leaves the jobs list).</li>
              </ul>
              <p className="review-modal__warn">This rewrites the records and can’t be undone from here.</p>
              {error ? <p className="leads__error" role="alert">{error}</p> : null}
            </>
          ) : null}
        </div>

        <div className="review-modal__foot">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={committing}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={confirm} loading={committing} disabled={loading || !preview}>
            {preview ? `Merge into #${preview.winner_job_number}` : "Merge"}
          </Button>
        </div>
      </div>
    </div>
  );
}
